import React from "react";
import { Shuffle, Eraser, Play, Square, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toMinor } from "./credits";
import { VIcon } from "./VCredit";
import { AdvancedAutoSettings } from '../originals/AdvancedAutoSettings';
import { OriginalsAccessNotice } from '../originals/OriginalsAccessNotice';
import { AutoBetCountInput } from '../originals/AutoBetCountInput';

export const KenoControls = ({ game, desktopStatsDockRef }) => {
  const { mode, setMode, bet, setBet, risk, setRisk, busy, auto, wallet, walletLoading } = game;
  const locked = busy || auto || game.picking;
  const minor = toMinor(bet);
  const disabled = !auto && (locked || game.access?.blocked || game.access?.loading || game.access?.error || walletLoading || !game.fairness || !game.config || !game.selected.length || !minor || minor > wallet.balance_minor || minor > game.config.max_bet_minor);
  const scaleBet = (factor) => {
    if (!game.config || walletLoading) return;
    const ceiling = factor > 1 ? Math.min(wallet.balance_minor, game.config.max_bet_minor) : game.config.max_bet_minor;
    const next = Math.min(ceiling, Math.max(1, Math.round((minor || 100) * factor)));
    setBet((next / 100).toFixed(2));
  };
  return (
    <aside className="keno-controls" data-testid="keno-controls">
      <div className="keno-modes" role="tablist" aria-label="Play mode" data-testid="keno-mode-segmented-control">
        {["manual", "auto"].map((value) => <button key={value} role="tab" aria-selected={mode === value} disabled={locked} data-testid={`keno-tab-${value}`} onClick={() => setMode(value)} className={mode === value ? "active" : ""}>{value === "manual" ? "Manual" : "Auto"}</button>)}
      </div>
      <div className="keno-field">
        <label htmlFor="keno-bet">Bet Amount</label>
        <div className="keno-bet-field">
          <VIcon testid="keno-bet-v-icon" />
          <Input id="keno-bet" data-testid="keno-bet-input" value={bet} onChange={(event) => setBet(event.target.value)} type="number" min="0.01" max={game.config ? game.config.max_bet_minor / 100 : undefined} step="0.01" inputMode="decimal" disabled={locked} />
          <button data-testid="keno-half-bet-button" onClick={() => scaleBet(0.5)} disabled={locked || walletLoading || !game.config} title="Half bet" aria-label="Half bet">½</button>
          <button data-testid="keno-double-bet-button" onClick={() => scaleBet(2)} disabled={locked || walletLoading || !game.config} title="Double bet up to your available balance" aria-label="Double bet">2×</button>
        </div>
      </div>
      <div className="keno-field">
        <label htmlFor="keno-risk">Difficulty</label>
        <div className="keno-risk-control"><select id="keno-risk" data-testid="keno-risk-select" value={risk} onChange={(event) => setRisk(event.target.value)} disabled={locked}>
          {["classic", "low", "medium", "high"].map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}
        </select><ChevronDown size={17} aria-hidden="true" className="keno-risk-chevron" /></div>
      </div>
      <div className="keno-pick-actions">
        <Button data-testid="keno-random-pick-button" onClick={game.randomPick} disabled={locked} className="keno-secondary"><Shuffle size={16} />{game.picking ? "Picking…" : "Random Pick"}</Button>
        <Button data-testid="keno-clear-button" onClick={game.clear} disabled={locked || !game.selected.length} className="keno-secondary"><Eraser size={16} />Clear Table</Button>
      </div>
      {mode === "auto" && <div className="keno-auto-options" data-testid="keno-auto-options">
        <label htmlFor="keno-round-count">Number of Bets</label>
        <AutoBetCountInput id="keno-round-count" testid="keno-auto-count" value={game.count} disabled={locked} onChange={game.setCount} />
        <AdvancedAutoSettings game={game} />
      </div>}
      <OriginalsAccessNotice game={game} />
      <Button data-testid="keno-bet-submit-button" onClick={game.start} disabled={disabled} className={`keno-bet-button ${auto ? "is-stopping" : ""}`}>
        {auto ? <Square size={16} /> : <Play size={16} />}{auto ? `Stop Auto (${game.remaining === null ? '∞' : game.remaining})` : busy ? "Drawing…" : mode === "auto" ? "Start Auto" : "Bet"}
      </Button>
      {!walletLoading && minor && minor > wallet.balance_minor && <p className="keno-notice" data-testid="keno-insufficient-balance">Insufficient V balance</p>}
      {game.config && minor > game.config.max_bet_minor && <p className="keno-notice" data-testid="keno-max-bet-error">Maximum bet: {(game.config.max_bet_minor / 100).toLocaleString('en-US')} V</p>}
      <div ref={desktopStatsDockRef} className="desktop-stats-dock" data-testid="keno-desktop-stats-dock" />
    </aside>
  );
};